import React, { useEffect } from 'react';


import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import { FormProvider } from 'react-hook-form';

import type { AccountIndexSchema } from '@liscio/api';
import { Icon } from '@liscio/ui';

import {
  CreateAccountFormFields,
  useCreateAccountForm,
  useCreateAccountSubmit,
} from '../CreateAccountForm';

interface InlineCreateAccountPanelProps {
  prefillName: string;
  onBack: () => void;
  onCancel: () => void;
  onSuccess: (account: AccountIndexSchema) => void;
}

export function InlineCreateAccountPanel({
  prefillName,
  onBack,
  onCancel,
  onSuccess,
}: InlineCreateAccountPanelProps) {
  const formMethods = useCreateAccountForm();
  const { submitCreateAccount, isSubmitting } = useCreateAccountSubmit(
    formMethods,
    {
      source: 'account_contact_selector',
      onSuccess: (account) => {
        onSuccess(account);
      },
    }
  );

  useEffect(() => {
    if (prefillName) {
      formMethods.setValue('name', prefillName);
    }
  }, [prefillName, formMethods]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <IconButton size="small" onClick={onBack} aria-label="Back">
          <Icon icon="arrowLeft" fontSize="small" />
        </IconButton>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          New account
        </Typography>
      </Box>
      <Box sx={{ px: 2, py: 2, maxHeight: 360, overflow: 'auto' }}>
        <FormProvider {...formMethods}>
          <CreateAccountFormFields
            layout="responsive"
            enableAdditionalDetails={false}
          />
        </FormProvider>
      </Box>
      <Box
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          px: 2,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button
          onClick={onCancel}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submitCreateAccount}
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined
          }
          sx={{ textTransform: 'none' }}
        >
          Create & select
        </Button>
      </Box>
    </Box>
  );
}
